import React from "react";
import type { ValueType } from "react-select";
import { Box, makeStyles } from "@material-ui/core";
import clsx from "clsx";

import ButtonDropdown from "../../components/dropdown/ButtonDropdown";
import type { DropdownOption } from "../../components/dropdown/DropdownOption";
import type { DeprecatedTabProps } from "./DeprecatedTab";
import DeprecatedTab from "./DeprecatedTab";
import useCommonStyles from "./tabStyles";

export interface DeprecatedTabsProps {
  tabs: DeprecatedTabProps[];
  enabledTabs: Set<number | string>;
  defaultId: number | string;
  setEnabledTabs: (tabs: Set<number | string>) => void;
  addTab?: (id: number | string) => void;
  removeTab?: (id: number | string) => void;
}

const useStyles = makeStyles({
  root: {
    display: "flex",
    width: "100%",
    maxWidth: "380px",
  },
});

const fillIcon = (Icon: (props: React.SVGProps<SVGSVGElement>) => JSX.Element, selected: boolean) => {
  const selectedFill = "#1a7fda";
  const defaultFill = "#7D8696";
  return <Icon fill={selected ? selectedFill : defaultFill} />;
};

const DeprecatedTabs = ({
  tabs,
  enabledTabs,
  defaultId,
  setEnabledTabs,
  addTab,
  removeTab,
}: DeprecatedTabsProps): JSX.Element => {
  const commonClasses = useCommonStyles();
  const classes = useStyles();

  const [selected, setSelected] = React.useState<number | string>(defaultId);

  const dropdownOptions: DropdownOption[] = tabs.map(({ id, label }) => ({ value: id, label }));
  const sortedDropdownOptions: DropdownOption[] = dropdownOptions.sort((a, b) => (a.label > b.label ? 1 : -1));

  const selectedOptions: DropdownOption[] = dropdownOptions.filter((option: DropdownOption) =>
    enabledTabs.has(option.value),
  );

  const tabsToDisplay = Array.from(enabledTabs).map((tab) => {
    const tabIndex = tabs.findIndex((t) => tab.valueOf() === t.id);

    if (tabIndex !== -1) {
      const { id, icon, label, onClick } = tabs[tabIndex];
      return {
        id,
        icon,
        label,
        onClick,
      } as DeprecatedTabProps;
    }

    return {} as DeprecatedTabProps;
  });

  React.useEffect(() => {
    setSelected(defaultId);
  }, [defaultId]);

  return (
    <Box className={classes.root}>
      {tabsToDisplay.map(({ id, icon, label, onClick }) => {
        const isSelected = id === selected;
        const iconOnly = !isSelected && enabledTabs.size > 3;

        return (
          <DeprecatedTab
            key={id}
            id={id}
            icon={() => fillIcon(icon, isSelected)}
            label={label}
            selected={isSelected}
            iconOnly={iconOnly}
            onClick={() => {
              setSelected(id);
              if (onClick) onClick();
            }}
          />
        );
      })}
      <Box
        className={clsx(commonClasses.tab, commonClasses.iconOnly)}
        padding="0 !important"
        width={50}
        data-testid="filter-dropdown"
      >
        <ButtonDropdown
          options={sortedDropdownOptions}
          isMulti={true as unknown as false | undefined}
          onChange={(dropdownOption: ValueType<DropdownOption, false>) => {
            const newOptions = (dropdownOption as unknown as DropdownOption[]).map((option) => option.value);

            if (newOptions.length < 1) return;

            const added = newOptions.filter((newOption) => !enabledTabs.has(newOption))[0];

            if (added === undefined) {
              const removed = Array.from(enabledTabs).filter((tab) => !newOptions.includes(tab))[0];
              enabledTabs.delete(removed);

              if (removeTab) removeTab(removed);

              if (removed === selected) {
                const newTabs = Array.from(enabledTabs);
                const lastId = newTabs[newTabs.length - 1];
                const lastTab = tabs.filter((tab) => tab.id === lastId)[0];
                setSelected(lastId);
                if (lastTab.onClick) lastTab.onClick();
              }
            } else {
              if (newOptions.length > 5) {
                const lastSelectedOption = [...enabledTabs].pop() ?? -1;
                enabledTabs.delete(lastSelectedOption);
              }

              enabledTabs.add(added);
              if (addTab) addTab(added);
            }

            setEnabledTabs(new Set([...enabledTabs]));
          }}
          value={selectedOptions as unknown as DropdownOption}
        />
      </Box>
    </Box>
  );
};

export default DeprecatedTabs;
